import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./FloatingWebsiteNavigation.css";
import LogoCircular from "../../assets/svgs/logo circular1.png";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  FaHome,
  FaInfoCircle,
  FaEnvelope,
  FaQuestionCircle,
  FaShieldAlt,
  FaFileContract,
  FaChartLine,
  FaLanguage,
  FaSignInAlt,
} from "react-icons/fa";

const CLICK_DRAG_THRESHOLD_PX = 6;
const MOBILE_DRAG_THRESHOLD_PX = 12;
const BUTTON_SIZE = 80; // Match FloatingSidebarButton size
const MENU_LEFT = 0;
const MENU_TOP = -90;
const ORIGIN_LEFT = 0;
const ORIGIN_TOP = 90;
const ITEM_SIZE = 60; // bigger menu items to match request
const ITEM_PADDING = 6;
const FLIP_INSET_PX = 70; // bring flipped arc closer to the logo when opening left

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    function handle(ev) {
      if (ref.current && !ref.current.contains(ev.target)) onClose();
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("touchstart", handle);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("touchstart", handle);
    };
  }, [ref, onClose]);
}

export default function FloatingWebsiteNavigation() {
  const [open, setOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [openToLeft, setOpenToLeft] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { changeLanguage, language } = useLanguage();
  const { currentUser, userProfile } = useAuth();
  
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const langMenuRef = useRef(null);
  
  // Position state - start at top-left
  const positionRef = useRef({ x: 18, y: 18 });
  const [position, setPosition] = useState(positionRef.current);
  const pendingPosRef = useRef(positionRef.current);
  
  // Dragging state
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const startPointerRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const rafRef = useRef(null);
  const isMobileRef = useRef(false);
  
  useOutsideClose(wrapRef, () => setOpen(false));

  const handleMenuClick = useCallback((route) => {
    setOpen(false);
    if (route) navigate(route);
  }, [navigate]);

  const handleLanguageToggle = useCallback(() => {
    setLangMenuOpen(prev => !prev);
  }, []);

  const handleLanguageSelect = useCallback((code) => {
    changeLanguage(code);
    setLangMenuOpen(false);
  }, [changeLanguage]);

  const handleAuthClick = useCallback(() => {
    setOpen(false);
    if (currentUser) {
      // User is authenticated, navigate to feed
      navigate('/feed');
    } else {
      // User is not authenticated
      if (location.pathname === '/') {
        // Already on authentication page → open mobile auth dropdown
        const event = new CustomEvent('openMobileAuth', {
          detail: { source: 'floatingNav' },
          bubbles: true
        });
        document.dispatchEvent(event);
      } else {
        // Go to authentication page first, then open dropdown after navigation
        navigate('/');
        // Use setTimeout to ensure page has loaded
        setTimeout(() => {
          const event = new CustomEvent('openMobileAuth', {
            detail: { source: 'floatingNav' },
            bubbles: true
          });
          document.dispatchEvent(event);
        }, 100);
      }
    }
  }, [navigate, currentUser, location.pathname]);

  // Available languages
  const availableLanguages = useMemo(() => [
    { code: 'en', name: 'English',  english: 'English' },
    { code: 'fr', name: 'Français', english: 'French' },
    { code: 'ar', name: 'العربية',  english: 'Arabic' },
    { code: 'es', name: 'Español',  english: 'Spanish' },
    { code: 'de', name: 'Deutsch',  english: 'German' },
    { code: 'tr', name: 'Türkçe',   english: 'Turkish' },
    { code: 'zh', name: '中文',       english: 'Chinese' },
    { code: 'ko', name: '한국어',     english: 'Korean' },
    { code: 'ru', name: 'Русский',   english: 'Russian' },
    { code: 'hi', name: 'हिन्दी',     english: 'Hindi' },
    { code: 'ur', name: 'اردو',       english: 'Urdu' },
  ], []);

  const { translations: t } = useLanguage();
  
  // Navigation items with icons
  const navigationItems = useMemo(() => [
    {
      label: currentUser ? (t?.floatingNav?.profile || 'Profile') : (t?.floatingNav?.login || 'Login'),
      onClick: handleAuthClick,
      icon: currentUser ? (
        <img
          src={userProfile?.avatar_url || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K"}
          alt={userProfile?.display_name || currentUser?.user_metadata?.full_name || currentUser?.user_metadata?.name || "Profile"}
          className="floating-user-profile-image"
        />
      ) : <FaSignInAlt />,
      route: currentUser ? '/feed' : '/',
      isAuth: true,
    },
    {
      label: t?.floatingNav?.home || 'Home',
      onClick: () => handleMenuClick('/'),
      icon: <FaHome />,
      route: '/',
    },
    {
      label: t?.floatingNav?.about || 'About',
      onClick: () => handleMenuClick('/about'),
      icon: <FaInfoCircle />,
      route: '/about',
    },
    {
      label: t?.floatingNav?.contact || 'Contact',
      onClick: () => handleMenuClick('/contact'),
      icon: <FaEnvelope />,
      route: '/contact',
    },
    {
      label: t?.floatingNav?.faq || 'FAQ',
      onClick: () => handleMenuClick('/faq'),
      icon: <FaQuestionCircle />,
      route: '/faq',
    },
    {
      label: t?.floatingNav?.privacy || 'Privacy',
      onClick: () => handleMenuClick('/privacy-policy'),
      icon: <FaShieldAlt />,
      route: '/privacy-policy',
    },
    {
      label: t?.floatingNav?.terms || 'Terms',
      onClick: () => handleMenuClick('/terms-of-services'),
      icon: <FaFileContract />,
      route: '/terms-of-services',
    },
    {
      label: t?.floatingNav?.investment || 'Investment',
      onClick: () => handleMenuClick('/investment'),
      icon: <FaChartLine />,
      route: '/investment',
    },
    {
      label: t?.floatingNav?.language || 'Language',
      onClick: handleLanguageToggle,
      icon: <FaLanguage />,
      isLanguage: true,
    },
  ], [handleMenuClick, handleLanguageToggle, handleAuthClick, currentUser, userProfile, t]);

  // Arc layout
  const total = navigationItems.length;
  const radius = 150; 
  const startDeg = -75;
  const endDeg = 75;

  // Precompute positions for all arc items
  const positions = useMemo(() => {
    const arr = [];
    for (let i = 0; i < total; i++) {
      const tNorm = total === 1 ? 0.5 : i / (total - 1);
      const angleDeg = startDeg + (endDeg - startDeg) * tNorm;
      const theta = (angleDeg * Math.PI) / 180;
      arr.push({ x: Math.cos(theta) * radius, y: Math.sin(theta) * radius });
    }
    return arr;
  }, [total, radius, startDeg, endDeg]);

  // Orient arc based on side (flip horizontally when opening to the left)
  const orientedPositions = useMemo(() => {
    if (!openToLeft) return positions;
    return positions.map(p => ({ x: -p.x + FLIP_INSET_PX, y: p.y }));
  }, [positions, openToLeft]);

  // Detect if device is mobile/touch
  useEffect(() => {
    const checkMobile = () => {
      isMobileRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Bounds calculation so items never go off-screen
  const computeArcExtents = useCallback(() => {
    const xs = orientedPositions.map(p => p.x);
    const ys = orientedPositions.map(p => p.y);

    const minX = Math.min(...xs) - ITEM_PADDING;
    const maxX = Math.max(...xs) + ITEM_PADDING;
    const minY = Math.min(...ys) - ITEM_PADDING;
    const maxY = Math.max(...ys) + ITEM_PADDING;
    return { minX, maxX, minY, maxY };
  }, [orientedPositions]);

  // RAF apply pending position
  const applyPendingPos = useCallback(() => {
    rafRef.current = null;
    positionRef.current = pendingPosRef.current;
    setPosition(positionRef.current);
  }, []);
  
  const requestApply = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(applyPendingPos);
  }, [applyPendingPos]);

  // Compute drag limits to keep button and menu on screen
  const computeDragLimits = useCallback((vw, vh) => {
    const { minX, maxX, minY, maxY } = computeArcExtents();

    // Absolute positioning math (button top-left + menu/top + origin + item translate)
    const offsetX = MENU_LEFT + ORIGIN_LEFT;
    const offsetY = MENU_TOP + ORIGIN_TOP;

    const minPosXFromItems = -(offsetX + minX);
    const maxPosXFromItems = vw - (offsetX + maxX + ITEM_SIZE);

    const minPosYFromItems = -(offsetY + minY);
    const maxPosYFromItems = vh - (offsetY + maxY + ITEM_SIZE);

    // Keep the button fully visible too
    const minPosXFromButton = 0;
    const maxPosXFromButton = vw - BUTTON_SIZE;

    const minPosYFromButton = 0;
    const maxPosYFromButton = vh - BUTTON_SIZE;

    return {
      minX: Math.max(minPosXFromItems, minPosXFromButton),
      maxX: Math.min(maxPosXFromItems, maxPosXFromButton),
      minY: Math.max(minPosYFromItems, minPosYFromButton),
      maxY: Math.min(maxPosYFromItems, maxPosYFromButton),
    };
  }, [computeArcExtents]);

  // Pointer handlers for dragging
  const handlePointerDown = (e) => {
    if (e.button !== 0) return;
    const rect = buttonRef.current.getBoundingClientRect();
    
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
    
    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    startPointerRef.current = { x: clientX, y: clientY };
    movedRef.current = false;
    isDraggingRef.current = true;
    setIsDragging(true);

    document.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.addEventListener('pointerup', handlePointerUp, { passive: true });
    document.addEventListener('touchmove', handlePointerMove, { passive: true });
    document.addEventListener('touchend', handlePointerUp, { passive: true });

    e.preventDefault();
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;

    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
    
    const tentativeX = clientX - dragOffsetRef.current.x;
    const tentativeY = clientY - dragOffsetRef.current.y;

    const { minX, maxX, minY, maxY } = computeDragLimits(window.innerWidth, window.innerHeight);

    const clampedX = Math.max(minX, Math.min(tentativeX, maxX));
    const clampedY = Math.max(minY, Math.min(tentativeY, maxY));

    const dx = clientX - startPointerRef.current.x;
    const dy = clientY - startPointerRef.current.y;
    
    const threshold = isMobileRef.current ? MOBILE_DRAG_THRESHOLD_PX : CLICK_DRAG_THRESHOLD_PX;
    if (!movedRef.current && (dx * dx + dy * dy) > threshold * threshold) {
      movedRef.current = true;
    }

    pendingPosRef.current = { x: clampedX, y: clampedY };
    requestApply();
  };

  const handlePointerUp = () => {
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
    document.removeEventListener('touchmove', handlePointerMove);
    document.removeEventListener('touchend', handlePointerUp);
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  const handleToggle = () => {
    if (isAnimating) return;
    if (isDraggingRef.current || movedRef.current) return;

    setIsAnimating(true);
    // Choose opening side based on available horizontal space
    try {
      const vw = window.innerWidth;
      const rightSpace = vw - (positionRef.current.x + BUTTON_SIZE);
      const leftSpace = positionRef.current.x;
      setOpenToLeft(rightSpace < leftSpace);
    } catch (_) {
      const vw = window.innerWidth;
      const rightSpace = vw - (positionRef.current.x + BUTTON_SIZE);
      const leftSpace = positionRef.current.x;
      setOpenToLeft(rightSpace < leftSpace);
    }
    setOpen((v) => !v);
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        langMenuRef.current &&
        !langMenuRef.current.contains(event.target)
      ) {
        setOpen(false);
        setLangMenuOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Keep within viewport after resize
  useEffect(() => {
    const onResize = () => {
      const { minX, maxX, minY, maxY } = computeDragLimits(window.innerWidth, window.innerHeight);
      const { x, y } = positionRef.current;
      const clampedX = Math.max(minX, Math.min(x, maxX));
      const clampedY = Math.max(minY, Math.min(y, maxY));
      pendingPosRef.current = { x: clampedX, y: clampedY };
      requestApply();
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [computeDragLimits, requestApply]);

  return (
    <div
      className={`floating-website-nav-container ${isDragging ? 'dragging' : ''}`}
      style={{ transform: `translate3d(${position.x}px, ${position.y}px, 0)` }}
    >
      {/* Main Button */}
      <button
        ref={buttonRef}
        className={`floating-website-nav-button ${open ? 'open' : ''}`}
        onPointerDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        onClick={handleToggle}
        aria-label="Toggle website navigation"
        draggable={false}
        style={{ touchAction: 'none' }}
      >
        <img src={LogoCircular} alt="iWinto" className="floating-website-logo" draggable={false} />
      </button>

      {/* Half Circle Menu */}
      <div ref={menuRef} className={`floating-website-menu ${open ? 'open' : ''} ${openToLeft ? 'open-left' : 'open-right'}`}>
        {/* Arc origin */}
        <div className="website-menu-items-container">
          {navigationItems.map((item, i) => {
            const { x, y } = orientedPositions[i];
            const isActive = item.route && location.pathname === item.route;
            const isLanguageActive = item.isLanguage && langMenuOpen;
            const isAuthActive = item.isAuth && currentUser && location.pathname === '/feed';

            return (
              <button
                key={`nav-item-${i}`}
                className={`floating-website-menu-item ${isActive ? 'active' : ''} ${isLanguageActive ? 'lang-active' : ''} ${isAuthActive ? 'auth-active' : ''}`}
                onClick={item.onClick}
                style={{
                  '--x': `${x}px`,
                  '--y': `${y}px`,
                  '--delay': `${i * 0.07}s`,
                }}
              >
                <div className={`floating-website-menu-item-icon ${item.isAuth && currentUser ? 'profile-icon' : ''}`}>{item.icon}</div>
                <span className="floating-website-menu-item-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Language Selection Menu */}
      {langMenuOpen && open && (
        <div 
          ref={langMenuRef}
          className={`floating-language-menu ${openToLeft ? 'position-left' : 'position-right'}`}
        >
          <div className="floating-language-menu-header">
            <FaLanguage />
            <span>{t?.floatingNav?.selectLanguage || 'Select Language'}</span>
          </div>
          <div className="floating-language-menu-options">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                className={`floating-language-option ${lang.code === language ? 'selected' : ''}`}
                onClick={() => handleLanguageSelect(lang.code)}
              >
                <div className="lang-option-texts">
                  <div className="lang-option-name">{lang.name}</div>
                  <div className="lang-option-english">{lang.english}</div>
                </div>
                {lang.code === language && (
                  <div className="lang-option-check">✓</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


